import PageHeader from "@/components/PageHeader";
import { ResourceNotFound } from "@/components/ResourceNotFound";
import { Module } from "@/constants";
import { ContractorVisitsService } from "@/modules/projects/services/contractorVisits";
import { ContractorVisitEditorDialog } from "@/modules/projects/components/contractors-visits/components/ContractorVisitEditorDialog";
import { ContractorVisitConfirmDialog } from "@/modules/projects/components/contractors-visits/components/ContractorVisitConfirmDialog";
import { ContractorVisitDetails } from "@/modules/projects/components/contractors-visits/components/ContractorVisitDetails";
import { ContractorVisitFooter } from "@/modules/projects/components/contractors-visits/components/ContractorVisitFooter";
import { ContractorVisitLoader } from "@/modules/projects/components/contractors-visits/components/ContractorVisitLoader";
import { useQuery } from "@tanstack/react-query";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ProjectMenu } from "@/modules/projects/components/menu/ProjectMenu";

export const ContractorVisitDirectory = memo(() => {
  const { t } = useTranslation();
  const { contractorVisitId } = useParams() as {
    contractorVisitId: string;
  };

  const visitQuery = useQuery({
    queryKey: [Module.ContractorVisits, contractorVisitId],
    queryFn: () => ContractorVisitsService.get(contractorVisitId),
    enabled: !!contractorVisitId,
  });

  const visitData = visitQuery.data;
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const breadcrumbItems = useMemo(
    () => [
      {
        label: t("breadcrumb.home"),
        href: "/",
      },
      {
        label: t("projectContractorVisits.title.multiple"),
        href: "/projects/contractor-visits",
      },
      {
        label: t("projectContractorVisits.breadcrumb.edit"),
        href: `/projects/contractor-visits/${contractorVisitId}`,
      },
    ],
    [contractorVisitId, t],
  );

  if (visitQuery.isLoading || visitQuery.isPending) {
    return <ContractorVisitLoader reason="loading" />;
  }

  if (visitQuery.isError || !visitData) {
    return (
      <>
        <PageHeader breadcrumbItems={breadcrumbItems} />
        <ResourceNotFound />
      </>
    );
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />

      <ProjectMenu projectId={visitData.project_id} className="mb-2" />

      <ContractorVisitDetails visitData={visitData} />

      <ContractorVisitFooter
        visitData={visitData}
        contractorVisitId={contractorVisitId}
        onEditClick={() => setIsEditDialogOpen(true)}
        onConfirmClick={() => setIsConfirmDialogOpen(true)}
      />

      <ContractorVisitEditorDialog
        visit={visitData}
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      />

      <ContractorVisitConfirmDialog
        visit={visitData}
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
      />
    </>
  );
});
